import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title: "", desc: "", modules: "", duration: "", price_student: "", price_salarie: "", price_unique: "", order: "" };

export default function AdminFormations() {
  const [formations, setFormations] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = () =>
    api.get("/public/content")
      .then((r) => setFormations(r.data.formations || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!editId && !form.title.trim()) return toast.error("L'intitulé de la formation est requis.");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (String(v ?? "").trim()) fd.append(k, v);
      });
      if (editId) {
        await api.patch(`/admin/formations/${editId}`, fd);
        toast.success("Formation mise à jour : le site affiche maintenant son programme détaillé.");
      } else {
        await api.post("/admin/formations", fd);
        toast.success("Formation ajoutée : elle remplace la carte par défaut sur le site.");
      }
      setForm(EMPTY);
      setEditId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer la formation « ${f.title} » ? Les cartes par défaut réapparaîtront.`)) return;
    try {
      await api.delete(`/admin/formations/${f.id}`);
      toast.success("Formation supprimée.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startEdit = (f) => {
    setEditId(f.id);
    setShowForm(true);
    setForm({
      title: f.title || "", desc: f.desc || "", modules: (f.modules || []).join("\n"),
      duration: f.duration || "", price_student: f.price_student || "",
      price_salarie: f.price_salarie || "", price_unique: f.price_unique || "",
      order: f.order ?? "",
    });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-8" data-testid="admin-formations-view">
      <section>
        <h2 className="flex flex-wrap items-center justify-between gap-3 font-display text-xl font-bold text-korban-navy">
          Formations affichées sur le site
          {!showForm && (
            <button onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY); }} data-testid="formation-new-button"
              className="inline-flex items-center gap-2 rounded-lg bg-korban-navy px-4 py-2.5 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2">
              <Plus size={14} /> Ajouter une formation
            </button>
          )}
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Décrivez le programme (modules, durée) : les cartes du site afficheront le détail. Laissez la liste vide
          pour conserver les 5 formations par défaut.
        </p>

        {showForm && (
          <form onSubmit={submit} data-testid="formation-form" className="mt-4 grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 sm:p-6">
            <div className="sm:col-span-2">
              <label htmlFor="f-title" className="mb-1 block text-xs font-bold text-slate-600">Intitulé *</label>
              <input id="f-title" required minLength={3} value={form.title} onChange={set("title")}
                placeholder="Ex. Formation certifiante en Pratique de Gestion des RH"
                data-testid="formation-input-title" className="field field-light" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="f-desc" className="mb-1 block text-xs font-bold text-slate-600">Description courte</label>
              <input id="f-desc" value={form.desc} onChange={set("desc")}
                placeholder="Phrase de présentation" data-testid="formation-input-desc" className="field field-light" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="f-modules" className="mb-1 block text-xs font-bold text-slate-600">Modules (un module par ligne)</label>
              <textarea id="f-modules" rows={4} value={form.modules} onChange={set("modules")}
                placeholder={"Cadre juridique du travail\nGestion des contrats\nPaie et déclarations sociales"}
                data-testid="formation-input-modules" className="field field-light resize-none" />
            </div>
            <div>
              <label htmlFor="f-duration" className="mb-1 block text-xs font-bold text-slate-600">Durée</label>
              <input id="f-duration" value={form.duration} onChange={set("duration")}
                placeholder="Ex. 3 mois (week-ends)" data-testid="formation-input-duration" className="field field-light" />
            </div>
            <div>
              <label htmlFor="f-order" className="mb-1 block text-xs font-bold text-slate-600">Ordre d'affichage</label>
              <input id="f-order" type="number" value={form.order} onChange={set("order")}
                placeholder="0" data-testid="formation-input-order" className="field field-light" />
            </div>
            <div>
              <label htmlFor="f-pstudent" className="mb-1 block text-xs font-bold text-slate-600">Tarif Étudiant(e) (FCFA)</label>
              <input id="f-pstudent" value={form.price_student} onChange={set("price_student")}
                placeholder="Ex. 175 000" data-testid="formation-input-student" className="field field-light" />
            </div>
            <div>
              <label htmlFor="f-psalarie" className="mb-1 block text-xs font-bold text-slate-600">Tarif Salarié(e) (FCFA)</label>
              <input id="f-psalarie" value={form.price_salarie} onChange={set("price_salarie")}
                placeholder="Ex. 250 000" data-testid="formation-input-salarie" className="field field-light" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="f-punique" className="mb-1 block text-xs font-bold text-slate-600">Tarif unique (si applicable, FCFA)</label>
              <input id="f-punique" value={form.price_unique} onChange={set("price_unique")}
                placeholder="Ex. 325 000 — laissez vide si tarifs étudiants/salariés"
                data-testid="formation-input-unique" className="field field-light" />
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <button type="submit" disabled={busy} data-testid="formation-submit-button"
                className="inline-flex items-center gap-2 rounded-lg bg-korban-navy px-6 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {editId ? "Enregistrer les modifications" : "Ajouter au site"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY); }} data-testid="formation-cancel-button"
                className="rounded-lg bg-white px-5 py-3 text-sm font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50">
                Annuler
              </button>
            </div>
          </form>
        )}

        {!formations ? (
          <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : formations.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="formations-admin-empty">
            Liste vide : le site affiche les 5 formations par défaut (RH, Juriste-RH, RH-PAIE & Comptable-PAIE, Fiscalité, Charge de Recouvrement).
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {formations.map((f) => (
              <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`formation-row-${f.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-korban-navy">{f.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {(f.modules || []).length} module(s){f.duration ? ` · ${f.duration}` : ""}
                      {f.price_unique ? ` · ${f.price_unique} FCFA` : ` · Étudiant ${f.price_student || "—"} / Salarié ${f.price_salarie || "—"}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button onClick={() => startEdit(f)} data-testid={`formation-edit-${f.id}`}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 hover:bg-slate-50">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => remove(f)} data-testid={`formation-delete-${f.id}`}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 hover:bg-red-50">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="flex items-start gap-2 rounded-xl bg-korban-blue/10 p-4 text-xs leading-relaxed text-slate-600">
        <GraduationCap size={16} className="mt-0.5 shrink-0 text-korban-blue" />
        Les tarifs par défaut restent affichés sur les cartes sans tarif renseigné. Les cartes par défaut (RH,
        Juriste-RH, RH-PAIE & Comptable-PAIE, Fiscalité, Charge de Recouvrement) disparaissent dès que vous ajoutez
        votre propre liste.
      </p>
    </div>
  );
}
